/**
 * emoteService.js
 *
 * Self-contained emote system for Frankly profile pages.
 * Provides:
 *   - Context loading & caching    (loadEmoteContext, useEmoteContext)
 *   - Token ↔ HTML rendering       (renderEmotesInHtml, unrenderEmotesFromHtml)
 *   - Plain-text input helpers     (insertTokenIntoTextControl, makeEmoteKeyDownHandler, stripEmoteSentinels)
 *   - React components             (EmoteImg, EmotePickerModal, PlainEmoteInput, RichTextEmoteInput)
 *
 * Emote token format:  `:packIndex:emoteIndex:`  (both 1-based, JSON insertion order)
 *
 * Usage on a new page:
 *   import { loadEmoteContext, EmotePickerModal, renderEmotesInHtml, PlainEmoteInput, ... }
 *     from '../services/emoteService';
 */

import React from 'react';

// ─── Constants ────────────────────────────────────────────────────────────────

export const EMOTE_MANIFEST_URL = '/img/emotes/index.json';
export const EMOTE_BASE_URL     = '/img/emotes';

// ─── Module-level cache ───────────────────────────────────────────────────────

/** @type {{ packs: string[], emotesByPack: Record<string,Array<{filename:string,name:string}>> }|null} */
let _cachedCtx    = null;
let _inflightLoad = null;

// ─── Pure helpers ─────────────────────────────────────────────────────────────

export const normalizeEmotePackManifest = (manifest) => {
    if (!manifest) return [];
    if (Array.isArray(manifest))          return manifest.map(String).filter(Boolean);
    if (Array.isArray(manifest?.packs))   return manifest.packs.map(String).filter(Boolean);
    return [];
};

/**
 * Parse Emoticons.json into `{ filename, name }` entries, preserving JSON
 * insertion order (critical — token indices depend on this order).
 */
export const normalizeEmoteEntries = (emoticonsJson) => {
    const root = emoticonsJson?.Emoticons ?? emoticonsJson?.emoticons ?? emoticonsJson;
    if (!root || typeof root !== 'object') return [];
    return Object.entries(root).map(([key, meta]) => ({
        name:     String(meta?.Name ?? meta?.name ?? key),
        filename: String(meta?.File ?? meta?.file ?? key),
    }));
};

/**
 * Build URL candidates for a pack + filename.
 * - If filename already has an extension → one definitive URL.
 * - Otherwise → [gif, png] so EmoteImg can fall back via onError.
 *
 * We intentionally do NOT probe with HEAD requests — those are fragile (CORS,
 * auth, CDN quirks). EmoteImg handles the fallback at render time instead.
 */
export const buildEmoteSrcCandidates = (packName, filename) => {
    const cleanPack = encodeURIComponent(String(packName  || ''));
    const cleanFile = String(filename || '');
    const hasExt    = /\.[A-Za-z0-9]+$/.test(cleanFile);
    const base      = `${EMOTE_BASE_URL}/${cleanPack}/`;
    if (hasExt) return [`${base}${encodeURIComponent(cleanFile)}`];
    return ['gif', 'png'].map(ext => `${base}${encodeURIComponent(cleanFile)}.${ext}`);
};

/**
 * Resolve a token to the first candidate URL (used for static HTML rendering).
 * EmoteImg handles the gif→png fallback at runtime.
 */
export const buildEmoteSrc = ({ packs = [], emotesByPack = {} }, packIndex1, emoteIndex1) => {
    const packName = packs?.[packIndex1 - 1];
    if (!packName) return '';
    const list  = emotesByPack?.[packName];
    if (!Array.isArray(list)) return '';
    const entry = list[emoteIndex1 - 1];
    if (!entry) return '';
    const file  = typeof entry === 'string' ? entry : (entry?.filename || entry?.file || '');
    if (!file) return '';
    return buildEmoteSrcCandidates(packName, file)[0] ?? '';
};

// ─── Token regex ──────────────────────────────────────────────────────────────

export const emoteTokenRegex = /:(\d+):(\d+):/g;

// ─── HTML helpers ─────────────────────────────────────────────────────────────

const escapeHtmlAttr = (v) =>
    String(v ?? '').replace(/&/g,'&amp;').replace(/"/g,'&quot;').replace(/</g,'&lt;').replace(/>/g,'&gt;');

/**
 * Replace every `:p:e:` token in `html` with an `<img>`.
 * `height:1em` makes emotes scale with font-size at every size level.
 * `data-emote-fallback` stores the png candidate so the inline onerror handler
 * can switch without needing React.
 */
export const renderEmotesInHtml = (html, emoteCtx) => {
    if (!html) return '';
    const ctx = emoteCtx || {};
    return String(html).replace(emoteTokenRegex, (m, p, e) => {
        const pi = Number(p), ei = Number(e);
        if (!Number.isFinite(pi) || !Number.isFinite(ei)) return m;
        const packName = ctx?.packs?.[pi - 1] ?? '';
        const entry    = ctx?.emotesByPack?.[packName]?.[ei - 1];
        const file     = entry ? (typeof entry === 'string' ? entry : (entry?.filename || '')) : '';
        if (!file) return m;
        const candidates = buildEmoteSrcCandidates(packName, file);
        const src        = candidates[0];
        if (!src) return m;
        const fallback   = candidates[1] ? escapeHtmlAttr(candidates[1]) : '';
        const token      = `:${pi}:${ei}:`;
        const onerror    = fallback
            ? `this.onerror=null;this.src='${fallback}';`
            : 'this.style.display="none";';
        return (
            `<img ` +
            `src="${escapeHtmlAttr(src)}" ` +
            `alt="${escapeHtmlAttr(token)}" ` +
            `data-emote="${escapeHtmlAttr(token)}" ` +
            `class="inline-emote" ` +
            `style="display:inline-block;vertical-align:middle;height:1em;width:auto;" ` +
            `onerror="${onerror}" />`
        );
    });
};

// ─── Render cache (prevents img flicker on re-render) ─────────────────────────
// Same token string + same pack list → same HTML string reference, so React's
// dangerouslySetInnerHTML and img tags don't reload.
const _renderCache = new Map();

export const renderEmotesInHtmlMemo = (html, emoteCtx) => {
    if (!html) return '';
    const fp  = (emoteCtx?.packs ?? []).join(',');
    const key = `${html}\x00${fp}`;
    if (_renderCache.has(key)) return _renderCache.get(key);
    const result = renderEmotesInHtml(html, emoteCtx);
    if (_renderCache.size > 2000) _renderCache.delete(_renderCache.keys().next().value);
    _renderCache.set(key, result);
    return result;
};

/** Replace every `<img data-emote>` with its token text. */
export const unrenderEmotesFromHtml = (html) => {
    if (!html) return '';
    try {
        const doc  = new DOMParser().parseFromString(String(html), 'text/html');
        doc.querySelectorAll('img[data-emote]').forEach(img => {
            img.replaceWith(doc.createTextNode(img.getAttribute('data-emote') || img.getAttribute('alt') || ''));
        });
        return doc.body.innerHTML;
    } catch {
        return String(html);
    }
};

export const stripEmoteSentinels = (s) =>
    String(s ?? '').replace(/\uFEFF/g, '').replace(/\u200B/g, '');

// ─── Plain-text input helpers (for controlled <textarea>/<input> fallback) ────

/**
 * Insert a token into a plain controlled input at the cursor position.
 */
export const insertTokenIntoTextControl = ({ el, value, setValue, token }) => {
    const current = String(value ?? '');
    if (!el) { setValue(current + token); return; }
    const start = Number.isFinite(el.selectionStart) ? el.selectionStart : current.length;
    const end   = Number.isFinite(el.selectionEnd)   ? el.selectionEnd   : start;
    setValue(current.slice(0, start) + token + current.slice(end));
    const caret = start + token.length;
    requestAnimationFrame(() => {
        try { el.focus(); el.setSelectionRange(caret, caret); } catch (_) {}
    });
};

/**
 * keyDown handler that treats `:p:e:` tokens as atomic delete units in plain inputs.
 */
export const makeEmoteKeyDownHandler = (valueRef, setValueFn) => (e) => {
    if (e.key !== 'Backspace' && e.key !== 'Delete') return;
    const el = e.currentTarget;
    if (!el) return;
    const val   = String(valueRef() ?? '');
    const start = el.selectionStart ?? 0;
    const end   = el.selectionEnd   ?? start;
    if (start !== end) return;
    const TOKEN_RE = /:(\d+):(\d+):/g;
    if (e.key === 'Backspace' && start > 0) {
        let m; TOKEN_RE.lastIndex = 0;
        while ((m = TOKEN_RE.exec(val)) !== null) {
            if (m.index + m[0].length === start) {
                e.preventDefault();
                setValueFn(val.slice(0, m.index) + val.slice(start));
                const c = m.index;
                requestAnimationFrame(() => { try { el.focus(); el.setSelectionRange(c, c); } catch (_) {} });
                return;
            }
        }
    }
    if (e.key === 'Delete' && start < val.length) {
        let m; TOKEN_RE.lastIndex = 0;
        while ((m = TOKEN_RE.exec(val)) !== null) {
            if (m.index === start) {
                e.preventDefault();
                setValueFn(val.slice(0, start) + val.slice(m.index + m[0].length));
                requestAnimationFrame(() => { try { el.focus(); el.setSelectionRange(start, start); } catch (_) {} });
                return;
            }
        }
    }
};

// ─── Context loader ───────────────────────────────────────────────────────────

export const loadEmoteContext = async () => {
    if (_cachedCtx)    return _cachedCtx;
    if (_inflightLoad) return _inflightLoad;

    _inflightLoad = (async () => {
        const res = await fetch(EMOTE_MANIFEST_URL, { cache: 'no-store' });
        if (!res.ok) throw new Error(`Failed to load emote manifest (${res.status})`);
        const manifest = await res.json();
        const packs    = normalizeEmotePackManifest(manifest);

        const emotesByPack = {};
        await Promise.all(packs.map(async (p) => {
            try {
                const r = await fetch(`${EMOTE_BASE_URL}/${encodeURIComponent(p)}/Emoticons.json`, { cache: 'no-store' });
                if (!r.ok) return;
                emotesByPack[p] = normalizeEmoteEntries(await r.json()).filter(e => e.filename);
            } catch (_) {}
        }));

        _cachedCtx    = { packs, emotesByPack };
        _inflightLoad = null;
        return _cachedCtx;
    })();

    return _inflightLoad;
};

export const invalidateEmoteContext = () => { _cachedCtx = null; _inflightLoad = null; };

// ─── React hook ───────────────────────────────────────────────────────────────

export const useEmoteContext = () => {
    const [ctx, setCtx] = React.useState(_cachedCtx ?? null);
    React.useEffect(() => {
        if (_cachedCtx) { setCtx(_cachedCtx); return; }
        let cancelled = false;
        loadEmoteContext().then(c => { if (!cancelled) setCtx(c); }).catch(() => {});
        return () => { cancelled = true; };
    }, []);
    return ctx;
};

// ─── EmoteImg ─────────────────────────────────────────────────────────────────

/**
 * Renders a single emote image. Tries gif first, then png on error.
 */
export const EmoteImg = ({ packName, filename, alt, className, style }) => {
    const candidates = React.useMemo(
        () => buildEmoteSrcCandidates(packName, filename),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [packName, filename]
    );
    const [idx, setIdx] = React.useState(0);
    // Reset to first candidate when the source changes
    React.useEffect(() => setIdx(0), [candidates.join('|')]);

    const src = candidates[idx] ?? candidates[0];
    if (!src) return null;

    return (
        <img
            src={src}
            alt={alt ?? ''}
            className={className}
            style={style}
            onError={() => {
                if (idx + 1 < candidates.length) setIdx(idx + 1);
            }}
        />
    );
};

// ─── EmotePickerModal ─────────────────────────────────────────────────────────

export const EmotePickerModal = ({ open, onClose, onSelect, emoteCtx }) => {
    const [packs, setPacks] = React.useState([]);
    const [activePack, setActivePack] = React.useState('');
    const [emotes, setEmotes] = React.useState([]);
    const [packPreviews, setPackPreviews] = React.useState({});
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState('');

    React.useEffect(() => {
        if (!open) return;
        let cancelled = false;
        setLoading(true);
        setError('');
        loadEmoteContext()
            .then(ctx => {
                if (cancelled) return;
                setPacks(ctx.packs);
                setActivePack(prev => prev || ctx.packs[0] || '');
                const previews = {};
                for (const p of ctx.packs) {
                    previews[p] = (ctx.emotesByPack[p] ?? [])
                        .slice(0, 3)
                        .map(e => (typeof e === 'string' ? e : e.filename));
                }
                setPackPreviews(previews);
            })
            .catch(e => {
                if (!cancelled) setError(String(e?.message || e));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [open]);

    React.useEffect(() => {
        if (!open || !activePack) {
            setEmotes([]);
            return;
        }
        let cancelled = false;
        setLoading(true);
        setError('');
        loadEmoteContext()
            .then(ctx => {
                if (!cancelled) setEmotes(ctx.emotesByPack[activePack] ?? []);
            })
            .catch(e => {
                if (!cancelled) {
                    setError(String(e?.message || e));
                    setEmotes([]);
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [open, activePack]);

    const packIndex = React.useMemo(() => {
        const idx = packs.findIndex(p => p === activePack);
        return idx >= 0 ? idx + 1 : 1;
    }, [packs, activePack]);

    if (!open) return null;

    return (
        <div
            onMouseDown={onClose}
            role="dialog"
            aria-modal="true"
            className="fixed inset-0 z-[9999] bg-black/40 flex items-center justify-center p-3"
        >
            <div
                onMouseDown={e => e.stopPropagation()}
                className="w-full sm:w-[520px] sm:max-w-[92vw] bg-offwhite border border-black/30 rounded-xl shadow-lg overflow-hidden flex flex-col max-h-[85dvh]"
            >
                <div className="p-3 border-b border-black/20 flex items-center gap-2">
                    <div className="text-sm font-bold text-coal flex-1">Emotes</div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-coal/70 hover:text-coal text-sm font-bold"
                    >
                        Close
                    </button>
                </div>

                {error && (
                    <div className="p-3 text-sm text-red-700 border-b border-black/10 bg-red-50">
                        {error}
                    </div>
                )}

                <div className="flex flex-col sm:flex-row flex-1 min-h-0">
                    <div className="sm:w-[120px] border-b sm:border-b-0 sm:border-r border-black/10 overflow-auto">
                        <div className="p-2 flex sm:flex-col gap-2 flex-wrap">
                            {packs.map(p => (
                                <button
                                    key={p}
                                    type="button"
                                    onClick={() => setActivePack(p)}
                                    className={`border border-black/20 rounded-lg px-2 py-1.5 bg-white hover:bg-black/5 flex gap-1 items-center justify-center ${
                                        p === activePack ? 'ring-2 ring-gold' : ''
                                    }`}
                                    title={p}
                                >
                                    {(packPreviews[p] ?? []).map((fn, i) => (
                                        <EmoteImg
                                            key={`${p}-${fn}-${i}`}
                                            packName={p}
                                            filename={fn}
                                            alt=""
                                            className="w-6 h-6 object-contain"
                                        />
                                    ))}
                                </button>
                            ))}
                            {!packs.length && (
                                <div className="text-sm text-coal/70 px-2 py-2">No packs found.</div>
                            )}
                        </div>
                    </div>

                    <div className="p-3 flex-1 overflow-auto">
                        {loading && !emotes.length ? (
                            <div className="text-sm text-coal/70">Loading…</div>
                        ) : (
                            <div className="grid grid-cols-6 sm:grid-cols-8 gap-2">
                                {emotes.map((e, i) => {
                                    const list = emoteCtx?.emotesByPack?.[activePack] ?? [];
                                    const idx = list.findIndex(item =>
                                        typeof item === 'string' ? item === e.filename : item?.filename === e.filename
                                    );
                                    const token = `:${packIndex}:${idx >= 0 ? idx + 1 : i + 1}:`;
                                    const filename = typeof e === 'string' ? e : e.filename;
                                    return (
                                        <button
                                            key={`${activePack}:${filename}:${i}`}
                                            type="button"
                                            title={`${e.name ?? filename} (${token})`}
                                            onClick={() => onSelect(token)}
                                            className="border border-black/20 bg-white hover:bg-black/5 rounded p-1 flex items-center justify-center"
                                        >
                                            <EmoteImg
                                                packName={activePack}
                                                filename={filename}
                                                alt={e.name ?? filename}
                                                className="w-7 h-7 object-contain"
                                            />
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// ─── Shared contenteditable helpers ───────────────────────────────────────────
// Used by both PlainEmoteInput and RichTextEmoteInput.

const _normalizeHtml = (html) =>
    String(html ?? '').replace(/\u200B/g,'').replace(/\uFEFF/g,'').replace(/&nbsp;/g,' ').replace(/&lt;3/g,'<3');

/**
 * Save cursor position as a character offset where each emote <img> counts as 1.
 */
const _saveCursorOffset = (el) => {
    const sel = window.getSelection?.();
    if (!el || !sel || sel.rangeCount === 0) return null;
    const range = sel.getRangeAt(0);
    let count = 0;
    const walk = (node) => {
        if (node === range.startContainer) { count += range.startOffset; return true; }
        if (node.nodeType === Node.TEXT_NODE) { count += node.textContent.length; return false; }
        if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.tagName === 'IMG') { count += 1; return false; }
            for (const child of node.childNodes) { if (walk(child)) return true; }
        }
        return false;
    };
    walk(el);
    return count;
};

/**
 * Restore cursor from a character offset. Each emote <img> counts as 1.
 */
const _restoreCursorOffset = (el, offset) => {
    const sel = window.getSelection?.();
    if (!el || !sel || offset === null) return;
    let remaining = offset, found = false;
    const range = document.createRange();
    const walk  = (node) => {
        if (found) return;
        if (node.nodeType === Node.TEXT_NODE) {
            const len = node.textContent.length;
            if (remaining <= len) { range.setStart(node, remaining); range.collapse(true); found = true; }
            else remaining -= len;
            return;
        }
        if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.tagName === 'IMG') {
                if (remaining === 0) { range.setStartBefore(node); range.collapse(true); found = true; }
                else remaining -= 1;
                return;
            }
            for (const child of node.childNodes) walk(child);
        }
    };
    walk(el);
    if (!found) { range.selectNodeContents(el); range.collapse(false); }
    sel.removeAllRanges();
    sel.addRange(range);
};

/**
 * Insert an emote <img> (or fallback text node) at the current cursor position
 * inside a contenteditable element.
 */
const _insertEmoteIntoContenteditable = (el, token, emoteCtx) => {
    const m = String(token).match(/^:(\d+):(\d+):$/);
    if (!m) return;
    const pi  = Number(m[1]), ei = Number(m[2]);
    const src = Number.isFinite(pi) && Number.isFinite(ei)
        ? buildEmoteSrc(emoteCtx || {}, pi, ei)
        : '';

    el.focus();
    try {
        const sel = window.getSelection?.();
        if (!sel) return;
        let range;
        if (sel.rangeCount > 0 && el.contains(sel.getRangeAt(0).commonAncestorContainer)) {
            range = sel.getRangeAt(0);
        } else {
            range = document.createRange();
            range.selectNodeContents(el);
            range.collapse(false);
            sel.removeAllRanges();
            sel.addRange(range);
        }
        if (!range.collapsed) range.deleteContents();

        let node;
        if (src) {
            const packName = emoteCtx?.packs?.[pi - 1] ?? '';
            const entry    = emoteCtx?.emotesByPack?.[packName]?.[ei - 1];
            const file     = entry ? (typeof entry === 'string' ? entry : (entry?.filename || '')) : '';
            const candidates = buildEmoteSrcCandidates(packName, file);
            const fallback   = candidates[1] ?? '';

            node = document.createElement('img');
            node.setAttribute('src', src);
            node.setAttribute('alt', token);
            node.setAttribute('data-emote', token);
            node.setAttribute('class', 'inline-emote');
            node.setAttribute('style', 'display:inline-block;vertical-align:middle;height:1em;width:auto;');
            node.setAttribute('contenteditable', 'false');
            node.setAttribute('draggable', 'false');
            if (fallback) {
                node.onerror = () => { node.onerror = null; node.src = fallback; };
            }
        } else {
            node = document.createTextNode(token);
        }

        range.insertNode(node);
        const after = document.createRange();
        after.setStartAfter(node);
        after.collapse(true);
        sel.removeAllRanges();
        sel.addRange(after);
    } catch (_) {}
};

/**
 * keyDown handler for contenteditable: treat emote <img> nodes as single chars.
 */
const _makeContenteditableKeyDownHandler = (editorRef, onInputFn) => (e) => {
    if (e.key !== 'Backspace' && e.key !== 'Delete') return;
    const sel = window.getSelection?.();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (!range.collapsed) return;

    const isEmoteImg = n => n?.nodeType === 1 && n.tagName === 'IMG' && n.classList?.contains('inline-emote');

    const removeNode = (n) => {
        const { parentNode: par, previousSibling: prev, nextSibling: next } = n;
        n.remove();
        const r = document.createRange();
        if (next)      { next.nodeType === Node.TEXT_NODE ? r.setStart(next, 0) : r.setStartBefore(next); }
        else if (prev) { prev.nodeType === Node.TEXT_NODE ? r.setStart(prev, prev.textContent?.length || 0) : r.setStartAfter(prev); }
        else if (par)  { r.selectNodeContents(par); r.collapse(false); }
        r.collapse(true);
        sel.removeAllRanges();
        sel.addRange(r);
    };

    const { startContainer: con, startOffset: off } = range;

    if (con.nodeType === Node.TEXT_NODE) {
        if (e.key === 'Backspace' && off === 0 && isEmoteImg(con.previousSibling)) {
            e.preventDefault(); removeNode(con.previousSibling); onInputFn?.();
        } else if (e.key === 'Delete' && off === (con.textContent?.length || 0) && isEmoteImg(con.nextSibling)) {
            e.preventDefault(); removeNode(con.nextSibling); onInputFn?.();
        }
        return;
    }
    if (con.nodeType === Node.ELEMENT_NODE) {
        if (e.key === 'Backspace' && off > 0 && isEmoteImg(con.childNodes[off - 1])) {
            e.preventDefault(); removeNode(con.childNodes[off - 1]); onInputFn?.();
        } else if (e.key === 'Delete' && isEmoteImg(con.childNodes[off])) {
            e.preventDefault(); removeNode(con.childNodes[off]); onInputFn?.();
        }
    }
};

// ─── PlainEmoteInput ──────────────────────────────────────────────────────────

/**
 * `<PlainEmoteInput>` — a single-line contenteditable input that renders emote
 * tokens as inline images. No formatting toolbar. Looks and behaves like a
 * regular <input> but with emotes appearing inline as you type or insert them.
 *
 * Props:
 *   value            string    Token-form value (controlled)
 *   onChange         fn        Called with new token-form value on every change
 *   placeholder      string
 *   emoteCtx         object    Emote context
 *   maxLength        number    Max characters (default 300)
 *   disabled         boolean
 *   className        string    Extra classes on the contenteditable div
 *   onKeyDown        fn        Extra keyDown handler (called after emote handling)
 *   onOpenEmotePicker fn       If provided, shows 🙂 button
 *   onSubmit         fn        Called when Enter (without Shift) is pressed
 */
export const PlainEmoteInput = ({
                                    value,
                                    onChange,
                                    placeholder,
                                    emoteCtx,
                                    maxLength  = 300,
                                    disabled   = false,
                                    multiLine  = false,
                                    className  = '',
                                    onKeyDown,
                                    onOpenEmotePicker,
                                    onSubmit,
                                }) => {
    const editorRef      = React.useRef(null);
    const hasFocusRef    = React.useRef(false);
    const lastEmittedRef = React.useRef(null);

    const _sanitize = (html) => {
        const doc  = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
        const root = doc.body.firstChild;
        const out  = [];
        const walk = (node) => {
            if (node.nodeType === Node.TEXT_NODE) { out.push(node.textContent); return; }
            if (node.nodeType === Node.ELEMENT_NODE) {
                const tag = node.tagName.toLowerCase();
                if (tag === 'img' && node.classList?.contains('inline-emote')) {
                    out.push(node.outerHTML); return;
                }
                if (multiLine && (tag === 'br'))  { out.push('<br>'); return; }
                if (multiLine && (tag === 'div' || tag === 'p')) {
                    // block → prepend a <br> for each block break (except first)
                    if (out.length > 0) out.push('<br>');
                    for (const child of node.childNodes) walk(child);
                    return;
                }
                for (const child of node.childNodes) walk(child);
            }
        };
        walk(root);
        return out.join('');
    };

    const _emitChange = () => {
        const el = editorRef.current;
        if (!el) return;
        const tokenized = unrenderEmotesFromHtml(_sanitize(_normalizeHtml(el.innerHTML)));
        if (tokenized === lastEmittedRef.current) return;
        lastEmittedRef.current = tokenized;
        onChange(tokenized);
    };

    // Sync value → DOM when unfocused / external change
    React.useEffect(() => {
        const el = editorRef.current;
        if (!el) return;
        const forceSync = _normalizeHtml(value) === "";
        if (hasFocusRef.current && !forceSync) return;
        if (value === lastEmittedRef.current) return;
        const rendered = renderEmotesInHtml(_normalizeHtml(value), emoteCtx);
        if (_normalizeHtml(rendered) !== _normalizeHtml(el.innerHTML)) {
            el.innerHTML = rendered;
            lastEmittedRef.current = value;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const handleInput = () => {
        const el = editorRef.current;
        if (!el) return;
        // Prevent multi-line: collapse <br>/<div> to nothing
        const raw = el.innerHTML;
        const sanitized = _sanitize(_normalizeHtml(raw));
        if (sanitized !== _normalizeHtml(raw)) {
            const offset = _saveCursorOffset(el);
            el.innerHTML = sanitized;
            _restoreCursorOffset(el, offset);
        }
        _emitChange();
    };

    const emoteKeyDown = _makeContenteditableKeyDownHandler(editorRef, _emitChange);

    const handleKeyDown = (e) => {
        emoteKeyDown(e);
        if (!e.defaultPrevented && e.key === 'Enter' && !e.shiftKey) {
            if (!multiLine) {
                e.preventDefault();
                onSubmit?.();
                return;
            }
            // multiLine: allow natural Enter behaviour (inserts <br>)
        }
        onKeyDown?.(e);
    };

    const insertEmote = (token) => {
        if (!editorRef.current) return;
        _insertEmoteIntoContenteditable(editorRef.current, token, emoteCtx);
        _emitChange();
    };

    const isEmpty = !value;

    const Wrap = multiLine ? 'div' : 'span';
    const innerStyle = multiLine
        ? { display: 'block', width: '100%', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }
        : { display: 'inline-block', verticalAlign: 'middle', width: '100%' };
    const innerClass = multiLine
        ? `w-full outline-none bg-transparent whitespace-pre-wrap break-words leading-normal
           before:content-[attr(data-placeholder)] before:pointer-events-none 
           ${isEmpty ? 'before:block' : 'before:hidden'}
           ${disabled ? 'opacity-50 pointer-events-none' : ''}
           ${className}`
        : `flex-1 min-w-0 outline-none bg-transparent whitespace-nowrap overflow-x-auto leading-normal
           before:content-[attr(data-placeholder)] before:pointer-events-none before:absolute before:left-0
           ${isEmpty ? 'before:block' : 'before:hidden'}
           ${disabled ? 'opacity-50 pointer-events-none' : ''}
           ${className}`;

    return (
        <Wrap className={`relative ${multiLine ? 'w-full' : 'flex items-center flex-1 min-w-0'}`}>
            <span
                ref={editorRef}
                contentEditable={!disabled}
                suppressContentEditableWarning
                onInput={handleInput}
                onKeyDown={handleKeyDown}
                onFocus={() => { hasFocusRef.current = true; }}
                onBlur={() => { hasFocusRef.current = false; }}
                data-placeholder={placeholder}
                className={innerClass}
                style={innerStyle}
            />
            {typeof onOpenEmotePicker === 'function' && !disabled && (
                <button
                    type="button"
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => onOpenEmotePicker(insertEmote)}
                    className={`${multiLine ? 'absolute bottom-2 right-2' : 'shrink-0 ml-1'} px-2 py-1 border border-black/20 bg-offwhite rounded text-xs font-bold text-coal/70 hover:text-coal hover:bg-black/5`}
                    title="Insert emote"
                >
                    🙂
                </button>
            )}
        </Wrap>
    );
};

// ─── RichTextEmoteInput ───────────────────────────────────────────────────────

/**
 * `<RichTextEmoteInput>` — contenteditable editor with full formatting toolbar
 * and emote insertion. Used for post creation.
 *
 * Props:
 *   value, onChange, placeholder, isOwnProfile, emoteCtx,
 *   maxChars, maxLines, onMetrics, onRefresh, refreshCount,
 *   onOpenEmotePicker, refreshDisabled
 */
export const RichTextEmoteInput = ({
                                       value,
                                       onChange,
                                       placeholder,
                                       isOwnProfile,
                                       emoteCtx,
                                       maxChars = 500,
                                       maxLines = 10,
                                       onMetrics,
                                       onRefresh,
                                       refreshCount  = 0,
                                       onOpenEmotePicker,
                                       refreshDisabled,
                                       onOpenFranklyCloud,
                                       attachmentSlot,
                                   }) => {
    const editorRef      = React.useRef(null);
    const hasFocusRef    = React.useRef(false);
    const lastEmittedRef = React.useRef(null);

    const [activeFormats, setActiveFormats] = React.useState({
        bold: false, italic: false, underline: false, strikeThrough: false, fontSize: '3',
    });
    const [metrics, setMetrics] = React.useState({ chars: 0, lines: 1 });

    const computeMetrics = (el) => {
        const text  = String(el?.innerText ?? '').replace(/\r/g, '');
        const emotes = el?.querySelectorAll ? el.querySelectorAll('img.inline-emote').length : 0;
        const fcImages = el?.querySelectorAll ? el.querySelectorAll('img.fc-image').length : 0;
        return { chars: text.length + (Number.isFinite(Number(emotes)) ? Number(emotes) : 0) + (fcImages * 150), lines: Math.max(1, text.split('\n').length) };
    };

    const insertCloudImage = React.useCallback((imgHtml) => {
        const el = editorRef.current;
        if (!el) return;
        const range = savedRangeRef.current;
        const sel = window.getSelection?.();
        if (range && sel) {
            try { sel.removeAllRanges(); sel.addRange(range); } catch (_) {}
        }
        const temp = document.createElement('div');
        temp.innerHTML = imgHtml;
        const img = temp.firstChild;
        if (!img) return;
        if (sel && sel.rangeCount > 0) {
            const r = sel.getRangeAt(0);
            r.insertNode(img);
            r.setStartAfter(img);
            r.collapse(true);
            sel.removeAllRanges();
            sel.addRange(r);
        } else {
            el.appendChild(img);
        }
        handleInput({ currentTarget: el });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const updateFormatting = () => {
        if (!editorRef.current) return;
        setActiveFormats({
            bold:          document.queryCommandState('bold'),
            italic:        document.queryCommandState('italic'),
            underline:     document.queryCommandState('underline'),
            strikeThrough: document.queryCommandState('strikeThrough'),
            fontSize:      document.queryCommandValue('fontSize') || '3',
        });
    };

    const syncMetrics = () => {
        const el = editorRef.current;
        if (!el) return;
        const m = computeMetrics(el);
        setMetrics(m);
        onMetrics?.(m);
    };

    // ── Sanitiser ──────────────────────────────────────────────────────────────
    const sanitize = (html) => {
        const parser = new DOMParser();
        const doc    = parser.parseFromString(`<div>${html}</div>`, 'text/html');
        const root   = doc.body.firstChild;
        const TAGS   = new Set(['b','i','u','strike','font','br','div','span','img']);
        const FONT_A = new Set(['size','color']);
        const IMG_A  = new Set(['src','alt','data-emote','class','style','contenteditable','draggable']);
        const clean  = (node) => {
            if (!node) return;
            for (const child of Array.from(node.childNodes)) {
                if (child.nodeType === Node.TEXT_NODE) continue;
                if (child.nodeType !== Node.ELEMENT_NODE) { child.remove(); continue; }
                const tag = child.tagName.toLowerCase();
                if (!TAGS.has(tag)) { child.replaceWith(doc.createTextNode(child.textContent || '')); continue; }
                if (tag === 'img') {
                    Array.from(child.attributes).forEach(a => { if (!IMG_A.has(a.name)) child.removeAttribute(a.name); });
                    continue;
                }
                if (tag === 'font') Array.from(child.attributes).forEach(a => { if (!FONT_A.has(a.name)) child.removeAttribute(a.name); });
                else if (tag !== 'br') Array.from(child.attributes).forEach(a => child.removeAttribute(a.name));
                clean(child);
            }
        };
        clean(root);
        return root.innerHTML || '';
    };

    // ── Font-size: manual wrapper ──────────────────────────────────────────────
    // execCommand('fontSize') skips contenteditable=false img nodes and also
    // fails silently when the cursor is inside an existing <font> wrapper.
    // We handle both cases ourselves.
    const applyFontSize = (sizeVal) => {
        const el = editorRef.current;
        if (!el) return;
        el.focus();
        const sel = window.getSelection?.();
        if (!sel || sel.rangeCount === 0) return;
        const range = sel.getRangeAt(0);

        if (!range.collapsed) {
            // ── Selection: extract, wrap in <font size="N">, reinsert ──────────
            const frag = range.extractContents();
            const font = document.createElement('font');
            font.setAttribute('size', sizeVal);
            font.appendChild(frag);
            range.insertNode(font);
            const newRange = document.createRange();
            newRange.selectNodeContents(font);
            sel.removeAllRanges();
            sel.addRange(newRange);
        } else {
            // ── Collapsed cursor: find & split ancestor <font> node, or just  ──
            // insert an empty <font size="N"> marker so the next typed char
            // inherits the new size. We also remove any empty same-size font
            // ancestors to avoid nesting confusion.

            // Walk up to find the nearest <font> ancestor within our editor
            let fontAncestor = null;
            let node = range.startContainer;
            while (node && node !== el) {
                if (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'FONT') {
                    fontAncestor = node;
                    break;
                }
                node = node.parentNode;
            }

            if (fontAncestor) {
                // Split the <font> ancestor at the cursor position
                // Get cursor position relative to the font node's text
                const splitRange = document.createRange();
                splitRange.setStart(fontAncestor, 0);
                splitRange.setEnd(range.startContainer, range.startOffset);
                const before = splitRange.extractContents(); // content before cursor

                // fontAncestor now only has content after cursor
                const afterFont = fontAncestor.cloneNode(false);
                while (fontAncestor.firstChild) afterFont.appendChild(fontAncestor.firstChild);

                // Build: [beforeFont][newFont(empty)][afterFont]
                const beforeFont = document.createElement('font');
                beforeFont.setAttribute('size', fontAncestor.getAttribute('size') || '3');
                beforeFont.appendChild(before);

                const newFont = document.createElement('font');
                newFont.setAttribute('size', sizeVal);
                // Insert a zero-width space so the cursor has a text node to sit in
                const zws = document.createTextNode('\u200B');
                newFont.appendChild(zws);

                const parent = fontAncestor.parentNode;
                parent.insertBefore(beforeFont, fontAncestor);
                parent.insertBefore(newFont, fontAncestor);
                parent.insertBefore(afterFont, fontAncestor);
                fontAncestor.remove();

                // Place cursor at start of afterFont (or inside newFont after ZWS)
                const cur = document.createRange();
                cur.setStart(zws, 1); // after the ZWS
                cur.collapse(true);
                sel.removeAllRanges();
                sel.addRange(cur);
            } else {
                // No font ancestor — use execCommand to set insertion-point size
                document.execCommand('fontSize', false, sizeVal);
            }
        }

        updateFormatting();
        const tokenized = unrenderEmotesFromHtml(sanitize(_normalizeHtml(el.innerHTML)));
        lastEmittedRef.current = tokenized;
        onChange(tokenized);
        requestAnimationFrame(syncMetrics);
    };

    // ── Format handler (bold/italic/underline/strikethrough) ──────────────────
    const handleFormat = (e, command, val = null) => {
        e.preventDefault();
        if (!editorRef.current) return;
        if (command === 'fontSize') { applyFontSize(val); return; }
        editorRef.current.focus();
        document.execCommand(command, false, val);
        updateFormatting();
        const tokenized = unrenderEmotesFromHtml(sanitize(_normalizeHtml(editorRef.current.innerHTML)));
        lastEmittedRef.current = tokenized;
        onChange(tokenized);
        requestAnimationFrame(syncMetrics);
    };

    const savedRangeRef = React.useRef(null);

    // ── Emote insertion ────────────────────────────────────────────────────────
    const insertEmoteToken = (token) => {
        const el = editorRef.current;
        if (!el) return;
        // Mark as focused so the value→DOM sync effect doesn't clobber our insert
        hasFocusRef.current = true;
        // Restore the saved selection range so the emote lands at the right spot
        // (inside any active font-size wrapper, not at position 0)
        el.focus();
        if (savedRangeRef.current) {
            try {
                const sel = window.getSelection?.();
                if (sel) {
                    sel.removeAllRanges();
                    sel.addRange(savedRangeRef.current);
                }
            } catch (_) {}
            savedRangeRef.current = null;
        }
        _insertEmoteIntoContenteditable(el, token, emoteCtx);
        const tokenized = unrenderEmotesFromHtml(sanitize(_normalizeHtml(el.innerHTML)));
        lastEmittedRef.current = tokenized;
        onChange(tokenized);
        requestAnimationFrame(syncMetrics);
    };

    // ── Sync value → DOM ──────────────────────────────────────────────────────
    React.useEffect(() => {
        const el = editorRef.current;
        if (!el) return;
        const forceSync = _normalizeHtml(value) === "";
        if (hasFocusRef.current && !forceSync) return;
        if (value === lastEmittedRef.current) return;
        const rendered = renderEmotesInHtml(_normalizeHtml(value), emoteCtx);
        if (_normalizeHtml(rendered) !== _normalizeHtml(el.innerHTML)) {
            el.innerHTML = rendered;
            lastEmittedRef.current = value;
        }
        syncMetrics();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    // ── Input handler ──────────────────────────────────────────────────────────
    const handleInput = (e) => {
        const el = e.currentTarget || editorRef.current;
        updateFormatting();
        const raw = el.innerHTML;
        const san = sanitize(_normalizeHtml(raw));
        if (san !== _normalizeHtml(raw)) {
            const offset = _saveCursorOffset(el);
            el.innerHTML = san;
            _restoreCursorOffset(el, offset);
        }
        const tokenized = unrenderEmotesFromHtml(sanitize(_normalizeHtml(el.innerHTML)));
        lastEmittedRef.current = tokenized;
        onChange(tokenized);
        requestAnimationFrame(syncMetrics);
    };

    // ── Backspace/Delete handler ───────────────────────────────────────────────
    const handleKeyDown = _makeContenteditableKeyDownHandler(
        editorRef,
        () => handleInput({ currentTarget: editorRef.current })
    );

    if (!isOwnProfile) return null;

    const over = metrics.chars > maxChars || metrics.lines > maxLines;

    const btn = (cmd, label, active, val = null) => (
        <button
            type="button"
            onMouseDown={e => handleFormat(e, cmd, val)}
            className={`w-7 h-7 flex items-center justify-center border border-black/20 rounded text-xs font-bold ${active ? 'bg-gold' : 'bg-offwhite hover:bg-black/5'}`}
        >
            {label}
        </button>
    );

    return (
        <div className="border-b border-black/20 bg-black/5 p-2 flex flex-col gap-2">
            <div className="flex flex-wrap items-start sm:items-center gap-2">
                <div className="flex-1 min-w-0 flex items-center justify-between border border-black/20 bg-offwhite p-1 rounded">
                    <div className="flex flex-wrap items-center gap-1 min-w-0">
                        <div className="flex items-center gap-1 shrink-0">
                            {btn('bold', 'B', activeFormats.bold)}
                            {btn('italic', <span className="italic">I</span>, activeFormats.italic)}
                            {btn('underline', <span className="underline">U</span>, activeFormats.underline)}
                            {btn('strikeThrough', <span className="line-through">S</span>, activeFormats.strikeThrough)}
                        </div>

                        <div className="hidden sm:block w-px h-4 bg-black/20 mx-1" />

                        <div className="w-full sm:w-auto flex items-center gap-1 pt-1 sm:pt-0">
                            {btn('fontSize', <span className="text-sm">fF</span>, activeFormats.fontSize === '3', '3')}
                            {btn('fontSize', <span className="text-base">fF</span>, activeFormats.fontSize === '4', '4')}
                            {btn('fontSize', <span className="text-lg">fF</span>, activeFormats.fontSize === '5', '5')}
                            {btn('fontSize', <span className="text-xl">fF</span>, activeFormats.fontSize === '6', '6')}
                        </div>

                        {typeof onOpenEmotePicker === 'function' && (
                            <>
                            <div className="hidden sm:block w-px h-4 bg-black/20 mx-1" />
                            <button
                                type="button"
                                onMouseDown={e => {
                                e.preventDefault();
                                const sel = window.getSelection?.();
                                if (sel && sel.rangeCount > 0) {
                                try { savedRangeRef.current = sel.getRangeAt(0).cloneRange(); } catch (_) {}
                            }
                            }}
                            onClick={() => onOpenEmotePicker(insertEmoteToken)}
                            className="px-2 h-full flex items-center justify-center border border-black/20 bg-offwhite rounded text-base font-bold text-coal/70 hover:text-coal hover:bg-black/5 shrink-0"
                            title="Insert emote"
                            >
                                🙂
                            </button>
                            </>
                        )}
                    </div>

                    <div
                        className={`px-2 text-[10px] font-bold tabular-nums shrink-0 self-start sm:self-auto ${
                            over ? 'text-red-600' : 'text-coal/60'
                        }`}
                    >
                        {metrics.chars}/{maxChars}
                    </div>
                </div>

                {typeof onOpenFranklyCloud === 'function' && (
                    <>
                        <button
                            type="button"
                            onMouseDown={e => {
                                e.preventDefault();
                                const sel = window.getSelection?.();
                                if (sel && sel.rangeCount > 0) {
                                    try { savedRangeRef.current = sel.getRangeAt(0).cloneRange(); } catch (_) {}
                                }
                            }}
                            onClick={() => onOpenFranklyCloud(insertCloudImage)}
                            className="flex items-center gap-1 p-3 px-2 h-[38px] border border-black/20 rounded bg-offwhite hover:bg-black/5 shrink-0"
                            title="FranklyCloud — insert or manage images"
                        >
                            <img src="/img/franklycloud.png" alt="" className="shrink-0" />
                            <span className="text-[10px] font-bold text-coal/70 hidden sm:inline">FranklyCloud</span>
                        </button>
                    </>
                )}

                {typeof onRefresh === 'function' && (
                    <button
                        type="button"
                        onClick={onRefresh}
                        disabled={refreshDisabled}
                        className={`relative h-[38px] px-2 flex items-center gap-1 border border-black/20 text-[10px] font-bold uppercase rounded shrink-0 ${
                            refreshDisabled ? 'bg-black/10 text-coal/40 cursor-not-allowed' : 'bg-offwhite text-coal hover:bg-black/5'
                        }`}
                    >
                        <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" className="shrink-0">
                            <path fill="currentColor" d="M17.65 6.35A7.95 7.95 0 0 0 12 4a8 8 0 1 0 7.75 6h-2.1A6 6 0 1 1 12 6c1.66 0 3.14.69 4.22 1.78L14 10h6V4l-2.35 2.35Z" />
                        </svg>
                        <span>Refresh</span>
                        {Number(refreshCount) > 0 && (
                            <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 flex items-center justify-center bg-red-600 text-white text-[10px] leading-none font-bold rounded-full">
          {Math.min(99, Number(refreshCount))}
        </span>
                        )}
                    </button>
                )}
            </div>

            <div className={attachmentSlot ? 'flex flex-col sm:flex-row gap-1 sm:items-stretch' : ''}>
                <div
                    ref={editorRef}
                    contentEditable
                    onInput={handleInput}
                    onKeyDown={handleKeyDown}
                    onKeyUp={() => { updateFormatting(); syncMetrics(); }}
                    onMouseUp={() => { updateFormatting(); syncMetrics(); }}
                    onFocus={() => { hasFocusRef.current = true; updateFormatting(); syncMetrics(); }}
                    onBlur={() => { hasFocusRef.current = false; }}
                    className={`min-h-[60px] max-h-[300px] overflow-y-scroll bg-offwhite border border-black/30 p-2 text-coal outline-none empty:before:content-[attr(data-placeholder)] empty:before:text-coal/50${attachmentSlot ? ' flex-1 min-w-0' : ''}`}
                    data-placeholder={placeholder}
                />
                {attachmentSlot}
            </div>
        </div>
    );
};

// ─── Eager pre-load ────────────────────────────────────────────────────────────
// Kick off context loading immediately when this module is first imported so
// the cache is warm (or in-flight) before any component mounts. This means
// useEmoteContext() returns the real context synchronously on first render
// instead of starting with null and causing a flicker re-render.
loadEmoteContext().catch(() => {});